import type { Setter } from "../../../util/setter/Setter.ts";
import type {AnalysisOptionProject} from "../AnalysisOptions.ts";
import Styles from "./ProjectSelection.module.scss";
import {type MouseEventHandler, useCallback} from "react";
import {useFieldSetter} from "../../../util/setter/FieldSetter.ts";
import {combineStyles} from "../../../util/combineStyles.ts";
import {Tooltip} from "@matillion/component-library";

interface Props {
    groupName: string
    projectName: string
    projectOptions: AnalysisOptionProject
    setProjectOptions: Setter<AnalysisOptionProject>
}
export function ProjectIndicator({groupName, projectName, projectOptions, setProjectOptions}: Props) {
    const [projects, setProjects] = useFieldSetter(projectOptions, setProjectOptions, "projectsToAnalyze")
    const onClick = useCallback<MouseEventHandler>((e) => {
        e.preventDefault()
        e.stopPropagation()
        // So we cycle between nothing selected, default selected, all selected
        setProjects((old)=> {
            const newOptions = [...old]
            const existingIndex = newOptions.findIndex(x => x.groupName === groupName && x.projectName === projectName)
            if(existingIndex === -1) {
                return [...old, {groupName, projectName, analyzeAllVersions: false, versionsToAnalyze: ["default"]}]
            } else {
                const newOne = {...newOptions[existingIndex]}
                newOptions[existingIndex] = newOne
                if(newOne.analyzeAllVersions) {
                    newOne.analyzeAllVersions = false
                    newOne.versionsToAnalyze = []
                } else if(newOne.versionsToAnalyze.length === 0) {
                    newOne.versionsToAnalyze = ["default"]
                } else {
                    newOne.analyzeAllVersions = true
                }
            }
            return newOptions
        })
    }, [setProjects, groupName, projectName])

    const found = projects.find(s => s.groupName === groupName && s.projectName === projectName)
    let text = "0"
    let tooltipText = "No versions scanned"
    let classes = [Styles.Indicator, Styles.Indicator_None]
    if(found) {
        if(found.analyzeAllVersions) {
            text = "✔️"
            tooltipText = "Scanning all versions"
            classes = [Styles.Indicator, Styles.Indicator_All]
        } else if(found.versionsToAnalyze.length === 1 && found.versionsToAnalyze[0] === "default") {
            text = "D"
            tooltipText = "Scanning default version"
            classes = [Styles.Indicator, Styles.Indicator_Default]
        } else if(found.versionsToAnalyze.length > 0) {
            text = found.versionsToAnalyze.length.toString()
            tooltipText = `Scanning ${text} versions`
            classes = [Styles.Indicator, Styles.Indicator_Some]
        }
    }
    return <Tooltip content={tooltipText}>
        <span className={combineStyles(...classes)} onClick={onClick}>
            {text}
        </span>
    </Tooltip>
}